import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Cancel, CheckCircle, Close, Edit as EditIcon, Error, HourglassEmpty, OpenInNew, PlayArrow, Refresh, Warning } from "@mui/icons-material";
import { isAnalysisStalled, TestResultStatus } from "@finvari/deliver";
import { useTestExecution, useTestSuites } from "../../../utils/testUtils";
import TestStatusChip from "../../../components/status/TestStatusChip";
import { createArtifactSession, markTestExecutionFailed, reanalyzeTestExecution, rerunTestExecutionSubject, setGoldenBaseline, startTestExecution, updateTestExecutionComments } from "../../../firebase";
import { useConfiguredEnvironments } from "../../../contexts/EnvironmentsContext";
import { useConfig } from "../../../contexts/ConfigContext";
import MarkdownContent from "../../../components/MarkdownContent";
import ManifestArtifactViewer from "./ManifestArtifactViewer";
import UserProfileDisplay from "../../../components/UserProfileDisplay";
import { formatSignature } from "./formatSignature";
import PatternReview from "./PatternReview";
import { usePatternAnalysis, usePatternAcks, useReviewProgress } from "./patternAnalysis";

const getStatusIcon = (status: TestResultStatus) => {
  switch (status) {
    case 'passed':
      return <CheckCircle color="success" fontSize="small" />;
    case 'failed':
    case 'error':
      return <Error color="error" fontSize="small" />;
    case 'running':
      return <PlayArrow color="primary" fontSize="small" />;
    case 'pending':
      return <HourglassEmpty color="disabled" fontSize="small" />;
    case 'skipped':
      return <Warning color="warning" fontSize="small" />;
    default:
      return <HourglassEmpty color="disabled" fontSize="small" />;
  }
};

const getStatusColor = (status: TestResultStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
    case 'error':
      return 'error';
    case 'running':
      return 'primary';
    case 'pending':
      return 'default';
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
};

const getArtifactFilename = (url: string) => {
  const parts = url.split('/');
  return parts[parts.length - 1];
};

function TestFailuresCard({ subjectResults }: { subjectResults: { subject: string; results?: Record<string, { status: string; message?: string; completedAt?: number }> }[] }) {
  const failures = subjectResults.flatMap(sr =>
    Object.entries(sr.results || {})
      .filter(([, r]) => r.status === 'failed' || r.status === 'error')
      .map(([step, r]) => ({
        subject: sr.subject,
        step,
        message: r.message,
        completedAt: r.completedAt,
      }))
  );
  if (failures.length === 0) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Failures"
        subheader={`${failures.length} failed test step${failures.length === 1 ? '' : 's'}`}
      />
      <CardContent>
        <Stack spacing={1}>
          {failures.map((f, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>{f.subject}</Typography>
              <Chip label={f.step} size="small" variant="outlined" />
              {f.completedAt && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(f.completedAt).toLocaleTimeString()}
                </Typography>
              )}
              {f.message && (
                <Typography variant="body2" color="error.main" sx={{ flexBasis: '100%' }}>
                  {f.message}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function TestExecutionDetail() {
  const { environmentId, testSuiteId } = useParams<{ environmentId: string; testSuiteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('executionId');
  const { testExecution, subjectResults, loading, error } = useTestExecution({ executionId, testSuiteId, environmentId });
  const { testSuites } = useTestSuites(testExecution?.rolloutId);
  const testSuite = testSuites.find(s => s.id === testExecution?.testSuiteId);
  const { environmentsById } = useConfiguredEnvironments();
  const { config } = useConfig();

  // Recompute pattern-verification analysis (only present once the aggregator has run for this
  // execution; hooks below no-op on executions that never ran the recompute lane).
  const { index: analysisIndex, analysisArtifactIndex, loading: analysisLoading, error: analysisError } =
    usePatternAnalysis(executionId, testExecution);
  const analysisReady = testExecution?.analysis?.status === 'ready' && analysisArtifactIndex >= 0;
  const { acks: patternAcks } = usePatternAcks(executionId, analysisReady);
  const reviewProgress = useReviewProgress(analysisIndex, patternAcks);

  // Data-match state for the finished-run banner and the per-subject matrix markers. The stored flag
  // (dataMismatchAcknowledged) lets the banner appear immediately, before analysis completes; the
  // analysis' snapshotMatchByProgram gives the per-program breakdown and the baseline's own snapshot
  // dates once it is ready.
  const snapshotMatch = analysisIndex?.baseline.snapshotMatchByProgram;
  const baselineSnapshots = analysisIndex?.baseline.baselineSnapshotByProgram;
  const mismatchedPrograms = snapshotMatch
    ? Object.keys(snapshotMatch).filter(s => snapshotMatch[s] === false)
    : [];
  const showDataMismatchBanner = !!testExecution?.dataMismatchAcknowledged || mismatchedPrograms.length > 0;

  // A 'pending' analysis can be a corpse: the analyzer runs in its own function and an OOM kill or
  // timeout leaves the status behind with nothing running to correct it. sweepStalledAnalysis writes
  // the same conclusion to the document within a few minutes, but re-evaluating the shared predicate
  // on a timer means the page shows the retry as soon as it is true rather than waiting for the sweep.
  const analysisPending = testExecution?.analysis?.status === 'pending';
  const [analysisClock, setAnalysisClock] = useState(() => Date.now());
  useEffect(() => {
    if (!analysisPending) return;
    const timer = setInterval(() => setAnalysisClock(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [analysisPending]);
  const analysisStalled = isAnalysisStalled(testExecution?.analysis, analysisClock);

  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [selectedArtifactIndex, setSelectedArtifactIndex] = useState(0);
  const [loadingArtifactIndex, setLoadingArtifactIndex] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [selectedArtifactFile, setSelectedArtifactFile] = useState<string | null>(null);
  const [markFailedConfirmOpen, setMarkFailedConfirmOpen] = useState(false);
  const [markingFailed, setMarkingFailed] = useState(false);
  const [markFailedError, setMarkFailedError] = useState<string | null>(null);
  const [rerunSubject, setRerunSubject] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [editingComments, setEditingComments] = useState(false);
  const [commentsdraft, setCommentsDraft] = useState('');
  const [savingComments, setSavingComments] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [settingGolden, setSettingGolden] = useState(false);
  const [goldenError, setGoldenError] = useState<string | null>(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);

  // Helper to build hash fragment from artifact index and optional file
  const buildArtifactHash = (index: number, file?: string | null) => {
    let hash = `#artifact=${index}`;
    if (file) hash += `&file=${encodeURIComponent(file)}`;
    return hash;
  };

  const handleViewArtifact = async (index: number, file?: string | null) => {
    try {
      setSessionError(null);
      setLoadingArtifactIndex(index);
      await createArtifactSession();
      setSelectedArtifactIndex(index);
      setSelectedArtifactFile(file ?? null);
      setArtifactDialogOpen(true);
      navigate(`${location.pathname}${location.search}${buildArtifactHash(index, file)}`, { replace: true });
    } catch (error) {
      console.error('Error creating artifact session:', error);
      setSessionError('Failed to create session for viewing artifact. Please try again.');
    } finally {
      setLoadingArtifactIndex(null);
    }
  };

  const handleCloseArtifact = () => {
    setArtifactDialogOpen(false);
    setSelectedArtifactFile(null);
    if (window.location.hash) {
      navigate(location.pathname + location.search, { replace: true });
    }
  };

  const handleArtifactFileSelect = useCallback((file: string | null) => {
    setSelectedArtifactFile(file);
    navigate(`${location.pathname}${location.search}${buildArtifactHash(selectedArtifactIndex, file)}`, { replace: true });
  }, [navigate, location.pathname, location.search, selectedArtifactIndex]);

  // Auto-open artifact dialog from URL hash on initial load
  const hashHandled = useRef(false);
  useEffect(() => {
    if (hashHandled.current || !testExecution?.artifacts?.length) return;
    hashHandled.current = true;
    // window.location is on purpose for clarity, we only run once per testExecution
    const match = window.location.hash.match(/^#artifact=(\d+)/);
    if (match) {
      const index = parseInt(match[1], 10);
      if (index >= 0 && index < testExecution.artifacts.length) {
        // Extract file parameter if present
        // window.location is on purpose for clarity, we only run once per testExecution
        const fileMatch = window.location.hash.match(/[&?]file=([^&]+)/);
        const file = fileMatch ? decodeURIComponent(fileMatch[1]) : null;
        handleViewArtifact(index, file);
      }
    }
    // Intentionally keyed only on testExecution: the hashHandled ref guards this
    // to run once per execution. Adding the recreated handleViewArtifact would
    // contradict that one-shot intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testExecution]);

  const handleRetry = async () => {
    if (!testExecution || retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await startTestExecution(
        testExecution.testSuiteId,
        testExecution.rolloutId,
        testExecution.subjects,
        testExecution.testSteps
      );
      const resultData = result.data as any;
      navigate(`/environment/${environmentId}/test-execution/${testSuiteId}?executionId=${resultData.executionId}`);
    } catch (error: any) {
      console.error('Error retrying test execution:', error);
      setRetryError(error.message || 'Failed to retry test execution');
    } finally {
      setRetrying(false);
    }
  };

  const handleMarkFailed = async () => {
    if (!testExecution || markingFailed) return;
    setMarkingFailed(true);
    setMarkFailedError(null);
    try {
      await markTestExecutionFailed(testExecution.executionId, testExecution.testSuiteId);
      setMarkFailedConfirmOpen(false);
    } catch (error: any) {
      console.error('Error marking test execution as failed:', error);
      setMarkFailedError(error.message || 'Failed to mark test execution as failed');
    } finally {
      setMarkingFailed(false);
    }
  };

  const handleConfirmRerun = async () => {
    if (!testExecution || !rerunSubject || rerunning) return;
    setRerunning(rerunSubject);
    setRerunError(null);
    try {
      await rerunTestExecutionSubject(testExecution.executionId, rerunSubject);
      setRerunSubject(null);
    } catch (err: any) {
      console.error('Error rerunning subject:', err);
      setRerunError(err.message || 'Failed to rerun subject');
    } finally {
      setRerunning(null);
    }
  };

  const isTerminal = testExecution?.status === 'completed'
    || testExecution?.status === 'failed'
    || testExecution?.status === 'cancelled';
  const subjectAllPassed = (subject: string) => {
    const sr = subjectResults.find(r => r.subject === subject);
    const stepResults = Object.values(sr?.results || {});
    return stepResults.length > 0 && stepResults.every(r => r.status === 'passed');
  };
  const rerunableSubjects = isTerminal && testSuite?.executionGranularity === 'subject'
    ? (testExecution?.subjects || []).filter(s => !subjectAllPassed(s))
    : [];
  const showRerunColumn = rerunableSubjects.length > 0;

  const handleStartEditComments = () => {
    setCommentsDraft(testExecution?.comments || '');
    setEditingComments(true);
  };

  const handleSaveComments = async () => {
    if (!testExecution || savingComments) return;
    setSavingComments(true);
    setCommentsError(null);
    try {
      await updateTestExecutionComments(testExecution.executionId, commentsdraft.trim());
      setEditingComments(false);
    } catch (error: any) {
      console.error('Error saving comments:', error);
      setCommentsError(error.message || 'Failed to save comments');
    } finally {
      setSavingComments(false);
    }
  };

  const handleReanalyze = async () => {
    if (!testExecution || reanalyzing) return;
    setReanalyzing(true);
    setReanalyzeError(null);
    try {
      await reanalyzeTestExecution(testExecution.executionId);
    } catch (error: any) {
      console.error('Error re-running pattern analysis:', error);
      setReanalyzeError(error.message || 'Failed to re-run analysis');
    } finally {
      setReanalyzing(false);
    }
  };

  const handleSetGolden = async () => {
    if (!testExecution || settingGolden) return;
    setSettingGolden(true);
    setGoldenError(null);
    try {
      await setGoldenBaseline(testExecution.testSuiteId, testExecution.executionId);
    } catch (error: any) {
      console.error('Error setting golden baseline:', error);
      setGoldenError(error.message || 'Failed to set golden baseline');
    } finally {
      setSettingGolden(false);
    }
  };

  // A completed no-baseline run is a reference snapshot and can be pinned as the suite's golden
  // baseline (the default comparison for future recompute runs).
  const isGoldenEligible = testExecution?.status === 'completed' && !testExecution?.baselineExecutionId;
  const isCurrentGolden = !!executionId && config?.goldenBaselines?.[testExecution?.testSuiteId ?? '']?.executionId === executionId;

  // Show "Mark as failed" button if execution has been in_progress for over 30 minutes
  const showMarkFailedButton = testExecution?.status === 'in_progress' &&
    testExecution.startedAt &&
    (Date.now() - new Date(testExecution.startedAt).getTime()) > 30 * 60 * 1000;

  if (!environmentId || !testSuiteId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Invalid test execution parameters.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading test execution...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!testExecution) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Test execution not found.
        </Alert>
      </Container>
    );
  }

  const progressPercentage = testExecution.totalTests > 0 
    ? (testExecution.completedTests / testExecution.totalTests) * 100 
    : 0;

  const logItemCount = analysisIndex?.items.filter(i => i.kind === 'log').length ?? 0;
  const navSections: { id: string; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    ...(analysisReady ? [{ id: 'patterns', label: `Patterns (${reviewProgress.accepted}/${reviewProgress.total})` }] : []),
    // Log templates are review items rendered inside the Patterns list; this entry jumps there.
    ...(analysisReady && logItemCount > 0 ? [{ id: 'patterns', label: `Logs (${logItemCount})` }] : []),
    { id: 'programs', label: 'Programs' },
    ...(testExecution.artifacts && testExecution.artifacts.length > 0 ? [{ id: 'artifacts', label: 'Artifacts' }] : []),
    { id: 'notes', label: 'Notes' },
  ];
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/environments')}
            sx={{ textDecoration: 'none' }}
          >
            Environments
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/environment/${environmentId}`)}
            sx={{ textDecoration: 'none' }}
          >
            {environmentId}
          </Link>
          <Typography variant="body2" color="text.primary">
            {testExecution.testSuiteName}
          </Typography>
        </Breadcrumbs>

        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {testExecution.testSuiteName}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Test execution for{' '}
            {testExecution.prNumber != null && testExecution.prNumber > 0 ? (
              <>
                <Link
                  component="button"
                  variant="subtitle1"
                  onClick={() => navigate(`/pull-request/${testExecution.prNumber}`)}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  PR #{testExecution.prNumber}
                </Link>
                {' on '}
              </>
            ) : null}
            <Link
              component="button"
              variant="subtitle1"
              onClick={() => navigate(`/environment/${environmentId}`)}
              sx={{ verticalAlign: 'baseline' }}
            >
              {environmentsById[environmentId!]?.name || environmentId}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Started {new Date(testExecution.createdAt).toLocaleString()} by{' '}
            <UserProfileDisplay uid={testExecution.createdBy} variant="body2" color="text.secondary" />
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Left navigation: jump between sections. */}
        <Box
          component="nav"
          sx={{ width: 180, flexShrink: 0, position: 'sticky', top: 16, display: { xs: 'none', md: 'block' } }}
        >
          <Stack spacing={0.5}>
            {navSections.map(s => (
              <Button
                key={s.id}
                size="small"
                onClick={() => scrollToSection(s.id)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
              >
                {s.label}
              </Button>
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Execution Status Card */}
      <Card id="overview" sx={{ mb: 3, scrollMarginTop: 16 }}>
        <CardHeader
          title="Execution Status"
          action={<TestStatusChip status={testExecution.status} />}
        />
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Progress: {testExecution.completedTests} of {testExecution.totalTests} tests completed
                </Typography>
                <Typography variant="body2">
                  {Math.round(progressPercentage)}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progressPercentage} />
            </Box>

            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Passed
                </Typography>
                <Typography variant="h6" color="success.main">
                  {testExecution.passedTests}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h6" color="error.main">
                  {testExecution.failedTests}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {testExecution.totalTests - testExecution.completedTests}
                </Typography>
              </Box>
            </Box>

            {/* Retry button for completed/failed executions */}
            {(testExecution.status === 'completed' || testExecution.status === 'failed') && (
              <Box>
                {retryError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setRetryError(null)}>
                    {retryError}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  startIcon={retrying ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={handleRetry}
                  disabled={retrying}
                >
                  Retry Test Execution
                </Button>
              </Box>
            )}

            {/* Mark as failed button for stalled executions */}
            {showMarkFailedButton && (
              <Box>
                {markFailedError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMarkFailedError(null)}>
                    {markFailedError}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={markingFailed ? <CircularProgress size={16} /> : <Cancel />}
                  onClick={() => setMarkFailedConfirmOpen(true)}
                  disabled={markingFailed}
                >
                  Mark as failed
                </Button>
              </Box>
            )}

            {/* Pin this baseline run as the suite's golden baseline (default for future recompute runs). */}
            {isGoldenEligible && (
              <Box>
                {goldenError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setGoldenError(null)}>
                    {goldenError}
                  </Alert>
                )}
                {isCurrentGolden ? (
                  <Chip icon={<CheckCircle />} color="success" variant="outlined" label="Golden baseline for this suite" />
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={settingGolden ? <CircularProgress size={16} /> : undefined}
                    onClick={handleSetGolden}
                    disabled={settingGolden}
                  >
                    Set as golden baseline
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Data-match banner: a recompute run whose environment held different data than the baseline
          produces diffs dominated by data drift that can't be separated from the code change. Shows
          from the stored flag immediately, and names the affected programs once analysis is ready. */}
      {showDataMismatchBanner && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            {mismatchedPrograms.length > 0
              ? `${mismatchedPrograms.length} program${mismatchedPrograms.length === 1 ? '' : 's'} ran against different data than the baseline`
              : 'This run compared against mismatched baseline data'}
          </Typography>
          <Typography variant="body2">
            Their diffs include data drift that cannot be separated from this PR&apos;s code changes, so
            treat the affected programs&apos; pattern differences with caution.
            {mismatchedPrograms.length > 0 && ` Affected: ${mismatchedPrograms.join(', ')}.`}
          </Typography>
        </Alert>
      )}

      {/* Baseline + recompute attestation summary */}
      {analysisReady && analysisIndex && (
        <Card sx={{ mb: 3, scrollMarginTop: 16 }}>
          <CardHeader
            title="Baseline & Recompute Attestation"
            subheader="What this run compared against, and evidence the recompute actually changed data"
          />
          <CardContent>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Baseline execution</Typography>
                  <Typography variant="body2">
                    {analysisIndex.baseline.executionId || 'unknown'}
                    {analysisIndex.baseline.ageHours != null && ` (${Math.round(analysisIndex.baseline.ageHours)}h old)`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Baseline environment</Typography>
                  <Typography variant="body2">{analysisIndex.baseline.environment || 'unknown'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Review progress</Typography>
                  <Typography variant="body2">
                    {reviewProgress.accepted} of {reviewProgress.total} items accepted
                  </Typography>
                </Box>
              </Box>
              {analysisIndex.attestation.some(a => !a.programDataAdvanced && a.expensesTouched === 0) && (
                <Alert severity="warning">
                  Some programs show no evidence of recompute (no program-data advance and zero expenses touched).
                  Review the attestation items in the Patterns tab.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Developer Comments Card */}
      <Card id="notes" sx={{ mb: 3, scrollMarginTop: 16 }}>
        <CardHeader
          title="Developer Notes"
          action={
            !editingComments ? (
              <IconButton size="small" aria-label="Edit developer notes" onClick={handleStartEditComments}>
                <EditIcon fontSize="small" />
              </IconButton>
            ) : undefined
          }
        />
        <CardContent>
          {editingComments ? (
            <Box>
              {commentsError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setCommentsError(null)}>
                  {commentsError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  value={commentsdraft}
                  onChange={(e) => setCommentsDraft(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  autoFocus
                  placeholder="Add context about this test run... (supports markdown)"
                  disabled={savingComments}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingComments(false);
                  }}
                />
                <Button size="small" variant="contained" onClick={handleSaveComments} disabled={savingComments}>
                  {savingComments ? <CircularProgress size={16} /> : 'Save'}
                </Button>
                <Button size="small" onClick={() => setEditingComments(false)} disabled={savingComments}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : testExecution.comments ? (
            <MarkdownContent>{testExecution.comments}</MarkdownContent>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No notes yet. You can click the edit button to add context about the run. For example, to differentiate different baselines or call attention to false positives.
            </Typography>
          )}
        </CardContent>
      </Card>

      <TestFailuresCard subjectResults={subjectResults} />

      {/* Pattern review: every diff group / attestation / log template must be accepted. */}
      {testExecution.analysis?.status === 'pending' && !analysisStalled && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Analyzing recompute diffs…</Typography>
            </Box>
          </CardContent>
        </Card>
      )}
      {(analysisStalled || testExecution.analysis?.status === 'error') && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleReanalyze} disabled={reanalyzing}>
              {reanalyzing ? <CircularProgress size={16} /> : 'Re-run analysis'}
            </Button>
          }
        >
          {analysisStalled
            ? 'The analysis was started but never finished — it most likely ran out of memory or hit its time limit. The test results themselves are unaffected.'
            : `Pattern analysis failed${testExecution.analysis?.error ? `: ${testExecution.analysis.error}` : ''}.`}
          {reanalyzeError && <Box sx={{ mt: 1, fontWeight: 600 }}>{reanalyzeError}</Box>}
        </Alert>
      )}
      {analysisReady && (
        <Card id="patterns" sx={{ mb: 3, scrollMarginTop: 16 }}>
          <CardHeader
            title="Patterns to Review"
            subheader={`${reviewProgress.accepted} of ${reviewProgress.total} accepted — every item must be accepted before this run can be verified`}
          />
          <CardContent>
            {analysisError && <Alert severity="error" sx={{ mb: 1 }}>{analysisError}</Alert>}
            {analysisLoading && !analysisIndex && <CircularProgress size={18} />}
            {analysisIndex && (
              <PatternReview
                executionId={executionId!}
                testExecution={testExecution}
                index={analysisIndex}
                analysisArtifactIndex={analysisArtifactIndex}
                acks={patternAcks}
              />
            )}
          </CardContent>
        </Card>
      )}

      {rerunError && (
        <Alert severity="error" onClose={() => setRerunError(null)}>
          {rerunError}
        </Alert>
      )}

      {/* Test Results Matrix */}
      <Card id="programs" sx={{ scrollMarginTop: 16 }}>
        <CardHeader
          title="Test Results Matrix"
          subheader={`${testExecution.subjects.length} subjects × ${testExecution.testSteps.length} test steps`}
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
                    Subject
                  </TableCell>
                  {testExecution.testSteps.map((testStep: string) => (
                    <TableCell
                      key={testStep}
                      align="center"
                      sx={{ fontWeight: 600, bgcolor: 'action.hover', minWidth: 120 }}
                    >
                      {testStep}
                    </TableCell>
                  ))}
                  {showRerunColumn && (
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover', width: 120 }} align="center">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {testExecution.subjects.map((subject: string) => {
                  const sig = testExecution.testDataSignatures?.[subject];
                  // Mark a subject whose data didn't match the baseline (per the analysis) so a reader
                  // of the matrix knows its diff is data-drift-dominated, mirroring the run dialog.
                  const isDataMismatch = snapshotMatch?.[subject] === false;
                  const baselineSig = baselineSnapshots?.[subject];
                  return (
                  <TableRow key={subject} hover>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
                      {isDataMismatch ? (
                        <Tooltip title={`This program ran against a snapshot from ${sig ? formatSignature(sig) : 'an unknown date'}, but the baseline used ${baselineSig ? formatSignature(baselineSig) : 'a different or unknown snapshot'}. Its diff is dominated by data drift.`}>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
                            <Warning fontSize="inherit" />
                            {subject}
                          </Box>
                        </Tooltip>
                      ) : (
                        subject
                      )}
                      {sig && (
                        <Typography variant="caption" color={isDataMismatch ? 'warning.main' : 'text.secondary'} display="block">
                          {formatSignature(sig)}
                        </Typography>
                      )}
                    </TableCell>
                    {testExecution.testSteps.map((testStep: string) => {
                      // Find the subject result document for this subject
                      const subjectResultDoc = subjectResults.find(sr => sr.subject === subject);
                      const result = subjectResultDoc?.results?.[testStep];
                      const duration = result?.startedAt && result?.completedAt ? (result.completedAt - result.startedAt) / 1000 : null;
                      if (!result) {
                        return (
                          <TableCell key={testStep} align="center">
                            -
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={testStep} align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              icon={getStatusIcon(result.status)}
                              label={result.status}
                              color={getStatusColor(result.status)}
                              size="small"
                              variant="outlined"
                            />
                            {result.message && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  maxWidth: 100, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={result.message}
                              >
                                {result.message}
                              </Typography>
                            )}
                            {duration && (
                              <Typography variant="caption" color="text.secondary">
                                {(duration).toFixed(1)}s
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    {showRerunColumn && (
                      <TableCell align="center">
                        {rerunableSubjects.includes(subject) && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={rerunning === subject ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
                            onClick={() => { setRerunError(null); setRerunSubject(subject); }}
                            disabled={rerunning !== null}
                          >
                            Rerun
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Status Legend:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="caption">Passed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Error color="error" fontSize="small" />
                <Typography variant="caption">Failed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlayArrow color="primary" fontSize="small" />
                <Typography variant="caption">Running</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HourglassEmpty color="disabled" fontSize="small" />
                <Typography variant="caption">Pending</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Warning color="warning" fontSize="small" />
                <Typography variant="caption">Skipped</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {testExecution.artifacts && testExecution.artifacts.length > 0 && (
        <Card id="artifacts" sx={{ mt: 3, scrollMarginTop: 16 }}>
          <CardHeader title="Test Artifacts" />
          <CardContent>
            <Stack spacing={2}>
              {sessionError && (
                <Alert severity="error" onClose={() => setSessionError(null)}>
                  {sessionError}
                </Alert>
              )}
              {testExecution.artifacts
                .map((artifact, index) => ({ artifact, index }))
                .sort((a, b) => (a.artifact.description || '').localeCompare(b.artifact.description || ''))
                .map(({ artifact, index }) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body1">
                    {artifact.description || `Test Report ${index + 1}`}
                  </Typography>
                  {artifact.type === 'external' ? (
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      component="a"
                      href={artifact.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={loadingArtifactIndex === index ? <CircularProgress size={16} /> : <OpenInNew />}
                      onClick={() => handleViewArtifact(index)}
                      disabled={loadingArtifactIndex !== null}
                    >
                      {loadingArtifactIndex === index ? 'Loading...' : 'View Results'}
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {testExecution.artifacts && testExecution.artifacts.length > 0 && (
        <Dialog
          open={artifactDialogOpen}
          onClose={handleCloseArtifact}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            {testExecution.artifacts[selectedArtifactIndex]?.description || 'Test Results'}
            <IconButton
              aria-label="close"
              onClick={handleCloseArtifact}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {testExecution.artifacts[selectedArtifactIndex]?.type === 'manifest' ? (
              <ManifestArtifactViewer
                executionId={executionId!}
                artifactIndex={selectedArtifactIndex}
                artifactUrl={testExecution.artifacts[selectedArtifactIndex].url}
                initialFile={selectedArtifactFile}
                onFileSelect={handleArtifactFileSelect}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <iframe
                  src={`/test-artifacts/${executionId}/${selectedArtifactIndex}/${getArtifactFilename(testExecution.artifacts[selectedArtifactIndex].url)}`}
                  sandbox="allow-same-origin allow-scripts"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1
                  }}
                  title="Test Artifact"
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
        </Box>
      </Box>
      {/* Mark as failed confirmation dialog */}
      <Dialog open={markFailedConfirmOpen} onClose={() => setMarkFailedConfirmOpen(false)}>
        <DialogTitle>Mark test execution as failed?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the test execution and all incomplete test subjects and steps as failed.
            The underlying build or test process will not be cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkFailedConfirmOpen(false)} disabled={markingFailed}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkFailed}
            color="error"
            variant="contained"
            disabled={markingFailed}
            startIcon={markingFailed ? <CircularProgress size={16} /> : undefined}
          >
            {markingFailed ? 'Marking...' : 'Mark as failed'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Rerun subject confirmation dialog */}
      <Dialog open={rerunSubject !== null} onClose={() => { if (!rerunning) setRerunSubject(null); }}>
        <DialogTitle>Rerun {rerunSubject}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Existing results for <strong>{rerunSubject}</strong> on this execution will be replaced.
            {(() => {
              const sr = subjectResults.find(r => r.subject === rerunSubject);
              const failed = sr?.failedTests ?? 0;
              const passed = sr?.passedTests ?? 0;
              if (sr) {
                return ` Last run: ${passed} passed, ${failed} failed — these will be cleared.`;
              }
              return null;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerunSubject(null)} disabled={rerunning !== null}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRerun}
            variant="contained"
            disabled={rerunning !== null}
            startIcon={rerunning !== null ? <CircularProgress size={16} /> : <Refresh />}
          >
            {rerunning !== null ? 'Rerunning...' : 'Rerun subject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

